import { JsPsych, JsPsychPlugin, ParameterType, TrialType } from "jspsych";
import {
  getLayoutConfigByDetectedLayout,
  mapDetectedLayoutToLayoutId,
} from "../config/keyboardLayoutConfig";

/**
 * The two physical key codes (event.code) we use as a "probe" to detect keyboard layout.
 * These keys are at the same physical location on all targeted keyboard layouts:
 * - Minus: key to the right of Zero
 * - KeyZ: key to the left of X
 */
const TARGET_KEY_CODES: string[] = [
  "Minus", "KeyZ",
];

const IMG_HQ_NEUTRAL = "/assets/images/menu/hqNeutral.svg";
const IMG_HQ_HAPPY = "/assets/images/menu/hqHappy.svg";
const CELEBRATE_AUDIO_URL = "/assets/audio/CG01_10_Celebrate_V1.wav";
const KEY_PRESS_AUDIO_URL = "/assets/audio/menu_keycap.mp3";
const UNSUPPORTED_PAGE_URL = "/unsupportedKeyboard.html";

/**
 * Expected event.key (lowercase) for each layout, based on physical key position.
 * Used to identify the keyboard layout from the collected mapping.
 * Matching is done against ALL target key positions to reduce false positives.
 */
type LayoutName =
  | "QWERTY_US"
  | "QWERTY_UK"
  | "QWERTZ_DE"
  | "QWERTZ_CH"
  | "AZERTY_FR";

type LayoutSignature = {
  name: LayoutName;
  expected: Record<string, string[]>;
};

const LAYOUT_SIGNATURES: LayoutSignature[] = [
  {
    name: "QWERTY_US",
    expected: {
      Minus: ["-"],
      KeyZ: ["z"],
    },
  },
  {
    name: "QWERTY_UK",
    expected: {
      Minus: ["-"],
      KeyZ: ["z"],
    },
  },
  {
    name: "QWERTZ_DE",
    expected: {
      Minus: ["?", "ß"],
      KeyZ: ["y"],
    },
  },
  {
    name: "QWERTZ_CH",
    expected: {
      Minus: ["'"],
      KeyZ: ["y"],
    },
  },
  {
    name: "AZERTY_FR",
    expected: {
      Minus: ["'", ")", "°"],
      KeyZ: ["w"],
    },
  },
];

function normalizeKeyValue(value?: string): string {
  return (value ?? "").toLowerCase();
}

function getMatchingLayouts(mapping: Record<string, string>): LayoutName[] {
  return LAYOUT_SIGNATURES.filter((signature) => {
    return TARGET_KEY_CODES.every((code) => {
      const observed = normalizeKeyValue(mapping[code]);
      const expectedValues = signature.expected[code] ?? [];
      return expectedValues.includes(observed);
    });
  }).map((signature) => signature.name);
}

function detectLayout(mapping: Record<string, string>): string {
  const candidates = getMatchingLayouts(mapping);

  if (candidates.length === 0) {
    return "UNKNOWN_LAYOUT";
  }

  // Return first matching layout as primary; candidates carry ambiguity when needed.
  return candidates[0];
}

function buildLayoutConfirmationMarkup(layoutCandidates: LayoutName[]): string {
  if (layoutCandidates.length === 1) {
    return `<span class="klp-layout-value">${layoutCandidates[0]}</span>`;
  }

  return layoutCandidates
    .map((layoutName) => `<span class="klp-layout-value">${layoutName}</span>`)
    .join(" or ");
}

// ---------------------------------------------------------------------------
// Probe keyboard: one step per key, shown sequentially
// ---------------------------------------------------------------------------

type KeyStepDef = {
  code: string;
  // `spokenName` is the word used in the instruction sentence (e.g. "Zero" instead
  // of the "0" glyph shown on the keycap itself, to avoid 0/O ambiguity when read).
  anchor: { code: string; display: string; spokenName: string };
  context: { code: string; display: string };
  direction: "left" | "right";
};

const KEY_STEPS: KeyStepDef[] = [
  {
    code: "KeyZ",
    anchor: { code: "KeyX", display: "X", spokenName: "X" },
    context: { code: "KeyC", display: "C" },
    direction: "left",
  },
  {
    code: "Minus",
    anchor: { code: "Digit0", display: "0", spokenName: "Zero" },
    context: { code: "Digit9", display: "9" },
    direction: "right",
  },
];

const TOTAL_STEPS = KEY_STEPS.length + 1; // + confirmation step
const AUTO_ADVANCE_DELAY_MS = 1500;
const CONFIRMATION_AUTO_ADVANCE_DELAY_MS = 4000;

// ---------------------------------------------------------------------------
// CSS
// ---------------------------------------------------------------------------

const PLUGIN_CSS = `
  .klp-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 14px 16px 18px;
    width: 100%;
    max-width: 560px;
    margin: 0 auto;
    box-sizing: border-box;
    font-family: "Inter", "Segoe UI", Arial, sans-serif;
    color: #fff;
  }
  .klp-avatar-frame {
    width: 88px;
    height: 88px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .klp-avatar {
    width: 88px;
    height: 88px;
    object-fit: contain;
  }
  .klp-avatar-arrival {
    animation: klp-avatar-arrival 560ms ease-out both;
    transform-origin: 50% 70%;
  }
  @keyframes klp-avatar-arrival {
    0% {
      transform: translateY(-8px) scale(0.92) rotate(-2deg);
    }
    18% {
      transform: translateY(0) scale(1.08) rotate(2deg);
    }
    32% {
      transform: translateY(-1px) scale(1.02) rotate(-2deg);
    }
    46% {
      transform: translateY(0) scale(1.03) rotate(2deg);
    }
    60% {
      transform: translateY(0) scale(1.01) rotate(-1deg);
    }
    74% {
      transform: translateY(0) scale(1.01) rotate(1deg);
    }
    100% {
      transform: translateY(0) scale(1) rotate(0deg);
    }
  }
  .klp-wrapper h2 {
    margin: 0 0 10px;
    font-size: 1.6em;
    font-weight: 800;
    color: #fff;
    letter-spacing: 0.02em;
    text-shadow:
      -1px -1px 0 #f08b2f,
      1px -1px 0 #f08b2f,
      -1px 1px 0 #f08b2f,
      1px 1px 0 #f08b2f;
  }
  .klp-progress {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
  }
  .klp-progress-pill {
    font-size: 0.95em;
    font-weight: 800;
    padding: 6px 16px;
    border-radius: 20px;
    background: rgba(0, 0, 0, 0.12);
    color: #fff;
    letter-spacing: 0.02em;
  }
  .klp-progress-dots {
    display: flex;
    gap: 8px;
    justify-content: center;
  }
  .klp-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transition: background 0.2s ease, transform 0.2s ease;
  }
  .klp-dot.is-active {
    background: #fff;
    transform: scale(1.15);
  }
  .klp-caption {
    /* Stretched to the full card width so text-align actually has room to move it.
       To nudge the text sideways while keeping it centered as a block, add e.g.
       "padding-left: 20px; padding-right: 0;" here (or the opposite to go left). */
    align-self: stretch;
    font-size: 1.15em;
    font-weight: 800;
    text-align: center;
    color: #fff;
    line-height: 1.45;
  }
  .klp-accent {
    color: #ffd93d;
    font-weight: 800;
  }
  .klp-arrow-row {
    display: flex;
    gap: 10px;
    justify-content: center;
    align-items: flex-end;
    margin-top: -8px;
  }
  .klp-arrow-cell {
    width: 76px;
    height: 26px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }
  /* Custom-drawn thick down-arrow (a glyph's stroke can't be made this bold) */
  .klp-text-arrow {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .klp-text-arrow .klp-arrow-shaft {
    width: 6px;
    height: 14px;
    border-radius: 3px;
    background: #f552b7;
  }
  .klp-text-arrow .klp-arrow-head {
    width: 0;
    height: 0;
    margin-top: -2px;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 11px solid #f552b7;
  }
  .klp-keys-row {
    display: flex;
    gap: 10px;
    justify-content: center;
    align-items: center;
    padding: 4px 0 8px;
  }
  .klp-key {
    --klp-key-shadow-color: #b7bdc2;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    border-radius: 8px;
    background: #eef1f3;
    box-shadow: 0 4px 0 var(--klp-key-shadow-color);
    border: 1px solid #dbdfe2;
    cursor: default;
    user-select: none;
    box-sizing: border-box;
    transform: translateY(0);
    transition: background 0.2s, box-shadow 0.2s, transform 0.14s ease-out;
    font-size: 1.5em;
    font-weight: 800;
    color: #555;
  }
  .klp-key.is-context {
    color: #888;
  }
  .klp-key.is-context.is-highlight {
    color: #444;
    background: #fbfdff;
    border-color: #e2e6ea;
  }
  .klp-key.is-pressing {
    animation: klp-key-press-large 185ms ease-out;
  }
  @keyframes klp-key-press-large {
    0% {
      transform: translateY(0);
      box-shadow: 0 4px 0 var(--klp-key-shadow-color);
    }
    45% {
      transform: translateY(4px);
      box-shadow: 0 0 0 var(--klp-key-shadow-color);
    }
    72% {
      transform: translateY(1px);
      box-shadow: 0 3px 0 var(--klp-key-shadow-color);
    }
    100% {
      transform: translateY(0);
      box-shadow: 0 4px 0 var(--klp-key-shadow-color);
    }
  }
  /* Detected key value shown after pressing - kept bold, it's the confirmation feedback */
  .klp-key-detected {
    font-family: "Inter", "Segoe UI", Arial, sans-serif;
    font-size: 1.3em;
    font-weight: 800;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #fff;
    line-height: 1;
  }
  /* Light pink: target key not yet pressed - solid fill, matches the other keys' depth */
  .klp-key.target-key {
    --klp-key-shadow-color: #ffb9db;
    background: #ffedf5;
    border: 1px solid #ffb9db;
  }
  /* Green: target key already recorded */
  .klp-key.pressed-key {
    --klp-key-shadow-color: #228b40;
    background: #3cb371;
    box-shadow: 0 4px 0 var(--klp-key-shadow-color);
    border: 1px solid #2e8b57;
  }
  /* Fixed min-height so revealing it never shifts the layout above/below */
  .klp-step-footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 52px;
    visibility: hidden;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  .klp-step-footer.is-visible {
    visibility: visible;
    pointer-events: auto;
    opacity: 1;
  }
  .klp-step-hint {
    font-size: 1em;
    font-weight: 800;
    color: #fff;
    background: rgba(0, 0, 0, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 999px;
    padding: 8px 14px;
    line-height: 1;
  }
  .klp-confirm-question {
    font-size: 1.2em;
    font-weight: 800;
    color: #fff;
    background: rgba(0, 0, 0, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 14px;
    padding: 14px 18px;
    line-height: 1.9;
    text-align: center;
  }
  .klp-layout-value {
    display: inline-block;
    /* Vertical margin matters here: when candidates wrap onto separate lines,
       the badge's own padding makes its box taller than the surrounding text's
       line-height, so without this the two badges end up touching. */
    margin: 5px 4px;
    padding: 3px 8px;
    border-radius: 8px;
    background: linear-gradient(135deg, #ffd46f 0%, #f9a63c 100%);
    color: #1e1e1e;
    font-family: "Inter", "Segoe UI", Arial, sans-serif;
    font-size: 0.9em;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .klp-confirm-actions {
    display: flex;
    gap: 10px;
    min-height: 40px;
    align-items: center;
  }
  .klp-button {
    padding: 10px 26px;
    font-size: 1.1em;
    font-weight: 800;
    color: #fff;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    box-shadow: 0 3px 0 rgba(0, 0, 0, 0.28);
    transition: background 0.15s;
    box-sizing: border-box;
  }
  .klp-button-yes {
    background: #2e8b57;
  }
  .klp-button-yes:hover {
    background: #267248;
  }
  .klp-button-no {
    background: #c04b5f;
  }
  .klp-button-no:hover {
    background: #a33d4f;
  }
  .klp-button:focus {
    outline: 3px solid #357abd;
    outline-offset: 2px;
  }
  @media (max-width: 480px) {
    .klp-key {
      width: 58px;
      height: 58px;
      font-size: 1.1em;
    }
    .klp-arrow-cell {
      width: 58px;
    }
  }
`;

// ---------------------------------------------------------------------------
// Markup builders
// ---------------------------------------------------------------------------

function buildHeaderHTML(avatarSrc: string): string {
  return `
    <div class="klp-avatar-frame">
      <img id="klp-avatar" class="klp-avatar" src="${avatarSrc}" alt="Keyboard helper" />
    </div>
    <h2>Keyboard Layout Detection</h2>
  `;
}

function buildDotsHTML(stepIndex: number): string {
  const dots = [0, 1, 2]
    .map((i) => `<span class="klp-dot${i <= stepIndex ? " is-active" : ""}"></span>`)
    .join("");

  return `
    <div class="klp-progress">
      <span class="klp-progress-pill">Step ${stepIndex + 1} of ${TOTAL_STEPS}</span>
      <div class="klp-progress-dots">${dots}</div>
    </div>
  `;
}

function buildKeyCellHTML(kind: "target" | "anchor" | "context", code: string, display: string): string {
  if (kind === "target") {
    // Deliberately blank - nothing is known about this key yet; the pink fill,
    // border, and arrow above are what mark it as "press here".
    return `<div class="klp-key target-key" data-code="${code}"></div>`;
  }
  const cls = kind === "anchor" ? "klp-key is-context is-highlight" : "klp-key is-context";
  return `<div class="${cls}" data-code="${code}">${display}</div>`;
}

function buildKeyRowHTML(step: KeyStepDef): string {
  const targetCell = buildKeyCellHTML("target", step.code, "");
  const anchorCell = buildKeyCellHTML("anchor", step.anchor.code, step.anchor.display);
  const contextCell = buildKeyCellHTML("context", step.context.code, step.context.display);

  const cells =
    step.direction === "left"
      ? [targetCell, anchorCell, contextCell]
      : [contextCell, anchorCell, targetCell];

  return `<div class="klp-keys-row">${cells.join("")}</div>`;
}

function buildArrowRowHTML(step: KeyStepDef): string {
  const targetCell =
    `<div class="klp-arrow-cell">` +
    `<span class="klp-text-arrow" aria-hidden="true">` +
    `<span class="klp-arrow-shaft"></span><span class="klp-arrow-head"></span>` +
    `</span>` +
    `</div>`;
  const spacerCell = `<div class="klp-arrow-cell"></div>`;

  const cells =
    step.direction === "left" ? [targetCell, spacerCell, spacerCell] : [spacerCell, spacerCell, targetCell];

  return `<div class="klp-arrow-row">${cells.join("")}</div>`;
}

function buildKeyStepHTML(stepIndex: number): string {
  const step = KEY_STEPS[stepIndex];

  return `
    <style>${PLUGIN_CSS}</style>
    <div class="klp-wrapper">
      ${buildHeaderHTML(IMG_HQ_NEUTRAL)}
      <div class="klp-caption">
        ${
          step.direction === "right"
            ? `Press the key immediately <span class="klp-accent">right</span><br />of the ${step.anchor.spokenName} key on your keyboard.`
            : `Press the key immediately<br /><span class="klp-accent">${step.direction}</span> of the ${step.anchor.spokenName} key on your keyboard.`
        }
      </div>
      ${buildArrowRowHTML(step)}
      ${buildKeyRowHTML(step)}
      ${buildDotsHTML(stepIndex)}
      <div id="klp-step-footer" class="klp-step-footer">
        <div class="klp-step-hint">Nice! Moving on&hellip;</div>
      </div>
    </div>
  `;
}

function buildConfirmationHTML(layoutCandidates: LayoutName[], isSupported: boolean): string {
  if (isSupported) {
    const detectedLayoutMarkup = buildLayoutConfirmationMarkup(layoutCandidates);

    return `
      <style>${PLUGIN_CSS}</style>
      <div class="klp-wrapper">
        ${buildHeaderHTML(IMG_HQ_HAPPY)}
        <div class="klp-confirm-question">Your keyboard is ${detectedLayoutMarkup}</div>
        ${buildDotsHTML(KEY_STEPS.length)}
        <div class="klp-step-hint">Nice! Moving on&hellip;</div>
      </div>
    `;
  }

  return `
    <style>${PLUGIN_CSS}</style>
    <div class="klp-wrapper">
      ${buildHeaderHTML(IMG_HQ_NEUTRAL)}
      <div class="klp-confirm-question">We couldn't detect a supported keyboard layout.</div>
      ${buildDotsHTML(KEY_STEPS.length)}
      <div class="klp-confirm-actions">
        <button id="klp-confirm-giveup" class="klp-button klp-button-no">Give up</button>
        <button id="klp-confirm-retry" class="klp-button klp-button-yes">Try again</button>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// jsPsych plugin info
// ---------------------------------------------------------------------------

const info = <const>{
  name: "keyboard-layout-detection",
  parameters: {
    participantID: {
      type: ParameterType.STRING,
      default: "",
    },
    studyID: {
      type: ParameterType.STRING,
      default: "",
    },
    sessionID: {
      type: ParameterType.STRING,
      default: "",
    },
  },
};

type Info = typeof info;

// ---------------------------------------------------------------------------
// Plugin class
// ---------------------------------------------------------------------------

/**
 * **keyboard-layout-detection**
 *
 * Guides the participant through 3 sequential steps: press the key to the
 * left of X (KeyZ), press the key to the right of Zero (Minus), then see the
 * detection result. Each key press auto-advances after a short delay (no
 * click needed). If a supported layout was detected, the result step also
 * auto-advances the same way, straight into finishing the trial. If it
 * wasn't supported, the participant picks "Try again" or "Give up".
 *
 * The participant presses each probe key; the plugin records
 * { event.code → event.key } for these 2 keys and infers the keyboard layout
 * (QWERTY_US / QWERTY_UK / QWERTZ_DE / QWERTZ_CH / AZERTY_FR).
 *
 * Trial data:
 *   - keyboard_mapping  — raw { code: key } object for the 2 probe keys (e.g. { Minus: "-", KeyZ: "y" })
 *   - detected_layout   — one of QWERTY_US | QWERTY_UK | QWERTZ_DE | QWERTZ_CH | AZERTY_FR | UNKNOWN_LAYOUT
 *   - detected_layout_candidates — all layouts that matched the 2-key signature
 */
class CustomKeyboardLayoutPlugin implements JsPsychPlugin<Info> {
  static info = info;

  constructor(private jsPsych: JsPsych) {}

  trial(display_element: HTMLElement, _trial: TrialType<Info>) {
    let mapping: Record<string, string> = {};
    const celebrationAudio = new Audio(CELEBRATE_AUDIO_URL);
    const keyPressAudio = new Audio(KEY_PRESS_AUDIO_URL);
    let detectedLayout = "UNKNOWN_LAYOUT";
    let detectedLayoutCandidates: LayoutName[] = [];
    let layoutConfirmedByUser: boolean | null = null;

    celebrationAudio.preload = "auto";
    keyPressAudio.preload = "auto";

    const finishTrial = () => {
      const layoutConfig = getLayoutConfigByDetectedLayout(detectedLayout);
      const layoutId = mapDetectedLayoutToLayoutId(detectedLayout);
      display_element.innerHTML = "";
      this.jsPsych.finishTrial({
        name: "keyboardLayoutDetection",
        keyboard_mapping: mapping,
        detected_layout: detectedLayout,
        detected_layout_candidates: detectedLayoutCandidates,
        layout_config_id: layoutId,
        layout_config: layoutConfig,
        layout_confirmed_by_user: layoutConfirmedByUser,
      });
    };

    const goToUnsupportedPage = () => {
      const layoutConfig = getLayoutConfigByDetectedLayout(detectedLayout);
      const layoutId = mapDetectedLayoutToLayoutId(detectedLayout);
      display_element.innerHTML = "";
      this.jsPsych.finishTrial({
        name: "keyboardLayoutDetection",
        keyboard_mapping: mapping,
        detected_layout: detectedLayout,
        detected_layout_candidates: detectedLayoutCandidates,
        layout_config_id: layoutId,
        layout_config: layoutConfig,
        layout_confirmed_by_user: false,
      });
      let unsupportedUrl =
        UNSUPPORTED_PAGE_URL +
        `?PARTICIPANT_ID=${encodeURIComponent(_trial.participantID)}` +
        `&STUDY_ID=${encodeURIComponent(_trial.studyID)}`;
      if (_trial.sessionID) {
        unsupportedUrl += `&SESSION_ID=${encodeURIComponent(_trial.sessionID)}`;
      }
      window.location.assign(unsupportedUrl);
    };

    const runKeyStep = (stepIndex: number) => {
      const step = KEY_STEPS[stepIndex];
      display_element.innerHTML = buildKeyStepHTML(stepIndex);

      // Use `let` so the callback can cancel its own listener once the target key is pressed
      // eslint-disable-next-line prefer-const
      let listener: any;

      listener = (this.jsPsych as any).customKeyboardResponse.getKeyboardResponse({
        callback_function: ({ keyCode, keyValueRaw, key }: { keyCode: string; keyValueRaw?: string; key: string }) => {
          if (keyCode !== step.code) return;

          (this.jsPsych as any).customKeyboardResponse.cancelKeyboardResponse(listener);

          keyPressAudio.currentTime = 0;
          keyPressAudio.play().catch(() => {
            // Ignore playback failures; the visual feedback still confirms the press.
          });

          const observedKeyValue = keyValueRaw ?? key;
          mapping[step.code] = observedKeyValue;

          const keyEl = display_element.querySelector<HTMLElement>(`[data-code="${step.code}"]`);
          if (keyEl) {
            keyEl.classList.remove("target-key");
            keyEl.classList.add("pressed-key");
            keyEl.innerHTML = "";
            const displayKey = observedKeyValue === " " ? "␣" : observedKeyValue.toUpperCase();
            const annotation = document.createElement("span");
            annotation.className = "klp-key-detected";
            annotation.textContent = displayKey;
            keyEl.appendChild(annotation);
            keyEl.classList.add("is-pressing");
            keyEl.addEventListener("animationend", () => {
              keyEl.classList.remove("is-pressing");
            }, { once: true });
          }

          const footer = display_element.querySelector<HTMLElement>("#klp-step-footer");
          footer?.classList.add("is-visible");

          const isLastKeyStep = stepIndex === KEY_STEPS.length - 1;

          // Auto-advance - no click needed. The pressed key stays visible (green)
          // for AUTO_ADVANCE_DELAY_MS before moving on.
          window.setTimeout(() => {
            if (isLastKeyStep) {
              detectedLayoutCandidates = getMatchingLayouts(mapping);
              detectedLayout = detectLayout(mapping);
              runConfirmationStep();
            } else {
              runKeyStep(stepIndex + 1);
            }
          }, AUTO_ADVANCE_DELAY_MS);
        },
        valid_responses: [step.code],
        persist: true,
        allow_held_key: false,
      });
    };

    const runConfirmationStep = () => {
      const isSupported = detectedLayoutCandidates.length > 0 && detectedLayout !== "UNKNOWN_LAYOUT";
      display_element.innerHTML = buildConfirmationHTML(detectedLayoutCandidates, isSupported);

      const avatar = display_element.querySelector<HTMLImageElement>("#klp-avatar");
      if (avatar) {
        avatar.classList.remove("klp-avatar-arrival");
        void avatar.offsetWidth;
        avatar.classList.add("klp-avatar-arrival");
      }

      if (isSupported) {
        celebrationAudio.currentTime = 0;
        celebrationAudio.play().catch(() => {
          // Ignore playback failures; the result should still be usable either way.
        });

        // No click needed - same pattern as the two key-press screens.
        window.setTimeout(() => {
          layoutConfirmedByUser = true;
          finishTrial();
        }, CONFIRMATION_AUTO_ADVANCE_DELAY_MS);
        return;
      }

      const retryBtn = display_element.querySelector<HTMLElement>("#klp-confirm-retry");
      const giveUpBtn = display_element.querySelector<HTMLElement>("#klp-confirm-giveup");

      retryBtn?.focus();
      retryBtn?.addEventListener("click", () => {
        mapping = {};
        detectedLayout = "UNKNOWN_LAYOUT";
        detectedLayoutCandidates = [];
        runKeyStep(0);
      });
      giveUpBtn?.addEventListener("click", () => {
        layoutConfirmedByUser = false;
        goToUnsupportedPage();
      });
    };

    runKeyStep(0);
  }
}

export default CustomKeyboardLayoutPlugin;
